/**
 * Hook para definir las columnas de las tablas de Tracking
 * Hook to define columns for Tracking tables
 */

import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OutdatedOrder, HistoryFailedRecord, UnmatchedRecord } from '../models/TrackingApiResponse';

export type TableType = 'outdatedOrders' | 'historyFailed' | 'unmatchedRecords';

// Helper function para formatear minutos desfasados
const formatMinutesOutdated = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`;
  } else if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} ${hours === 1 ? 'hora' : 'horas'}`;
    }
    return `${hours}h ${remainingMinutes}min`;
  } else {
    const days = Math.floor(minutes / 1440);
    const remainingHours = Math.floor((minutes % 1440) / 60);
    if (remainingHours === 0) {
      return `${days} ${days === 1 ? 'día' : 'días'}`;
    }
    return `${days}d ${remainingHours}h`;
  }
};

interface UseAgGridColumnsProps {
  tableType: TableType;
  selectedColumns?: string[];
}

export const useAgGridColumns = ({ tableType, selectedColumns = [] }: UseAgGridColumnsProps) => {
  const { t } = useTranslation('trackingModule');

  const allColumnDefs: ColDef[] = useMemo(() => {
    switch (tableType) {
      case 'outdatedOrders':
        return getOutdatedOrdersColumns(t);
      case 'historyFailed':
        return getHistoryFailedColumns(t);
      case 'unmatchedRecords':
        return getUnmatchedRecordsColumns(t);
      default:
        return [];
    }
  }, [tableType, t]);

  // Filtrar columnas según selectedColumns si se proporciona
  const filteredColumnDefs = useMemo(() => {
    if (selectedColumns.length > 0) {
      return allColumnDefs.filter((col) => {
        const field = col.field || '';
        return selectedColumns.includes(field);
      });
    }
    return allColumnDefs;
  }, [allColumnDefs, selectedColumns]);

  return {
    allColumnDefs,
    filteredColumnDefs,
  };
};

function getOutdatedOrdersColumns(t: any): CustomColDef[] {
  return [
    {
      field: 'order_id',
      headerName: t('table.order_id'),
      sortable: true,
      minWidth: 120,
    },
    {
      field: 'tracking_number',
      headerName: t('table.tracking_number'),
      sortable: true,
      minWidth: 180,
    },
    {
      field: 'courier_name',
      headerName: t('table.courier'),
      sortable: true,
      minWidth: 120,
      cellRenderer: (params: any) => {
        const data = params.data as OutdatedOrder;
        return <span className="text-title">{data.courier_name || '-'}</span>;
      },
    },
    {
      field: 'logistic_status',
      headerName: t('table.logistic_status'),
      sortable: true,
      minWidth: 150,
      cellRenderer: (params: any) => {
        const data = params.data as OutdatedOrder;
        if (!data.logistic_status) return '-';
        return <span className="text-title">{data.logistic_status.name}</span>;
      },
    },
    {
      field: 'last_shipment_update',
      headerName: t('table.last_shipment_update'),
      sortable: true,
      minWidth: 180,
      cellRenderer: (params: any) => {
        const data = params.data as OutdatedOrder;
        if (!data.last_shipment_update) return '-';
        return new Date(data.last_shipment_update).toLocaleString('es-ES');
      },
    },
    {
      field: 'minutes_outdated',
      headerName: t('table.minutes_outdated'),
      sortable: true,
      minWidth: 150,
      cellRenderer: (params: any) => {
        const data = params.data as OutdatedOrder;
        return <span className="text-title">{formatMinutesOutdated(data.minutes_outdated)}</span>;
      },
    },
  ];
}

function getHistoryFailedColumns(t: any): CustomColDef[] {
  return [
    {
      field: 'id',
      headerName: 'ID',
      sortable: true,
      minWidth: 80,
    },
    {
      field: 'order_id',
      headerName: t('table.order_id'),
      sortable: true,
      minWidth: 120,
    },
    {
      field: 'tracking_number',
      headerName: t('table.tracking_number'),
      sortable: true,
      minWidth: 180,
    },
    {
      field: 'courier_id',
      headerName: t('table.courier'),
      sortable: true,
      minWidth: 120,
    },
    {
      field: 'date_tracking_update_date',
      headerName: t('table.date_tracking_update_date'),
      sortable: true,
      minWidth: 150,
    },
    {
      field: 'error_message',
      headerName: t('table.error_message'),
      sortable: true,
      minWidth: 200,
      cellRenderer: (params: any) => {
        const data = params.data as HistoryFailedRecord;
        return (
          <span className="text-subtext" title={data.error_message}>
            {data.error_message || '-'}
          </span>
        );
      },
    },
    {
      field: 'retry_count',
      headerName: t('table.retry_count'),
      sortable: true,
      minWidth: 120,
      cellRenderer: (params: any) => {
        const data = params.data as HistoryFailedRecord;
        return <span className="text-title">{data.retry_count}</span>;
      },
    },
    {
      field: 'created_at',
      headerName: t('table.created_at'),
      sortable: true,
      minWidth: 180,
      cellRenderer: (params: any) => {
        const data = params.data as HistoryFailedRecord;
        if (!data.created_at) return '-';
        return new Date(data.created_at).toLocaleString('es-ES');
      },
    },
  ];
}

function getUnmatchedRecordsColumns(t: any): CustomColDef[] {
  return [
    {
      field: 'tracking_number',
      headerName: t('table.tracking_number'),
      sortable: true,
      minWidth: 220,
    },
    {
      field: 'order_id',
      headerName: t('table.order_id'),
      sortable: true,
      minWidth: 150,
      cellRenderer: (params: any) => {
        const data = params.data as UnmatchedRecord;
        return <span className="text-title">{data.order_id || '-'}</span>;
      },
    },
    {
      field: 'courier_id',
      headerName: t('table.courier'),
      sortable: true,
      minWidth: 150,
    },
    {
      field: 'source',
      headerName: t('table.source'),
      sortable: true,
      minWidth: 150,
      cellRenderer: (params: any) => {
        const data = params.data as UnmatchedRecord;
        return <span className="text-title capitalize">{data.source || '-'}</span>;
      },
    },
    {
      field: 'source_identifier',
      headerName: t('table.source_identifier'),
      sortable: true,
      minWidth: 280,
      cellRenderer: (params: any) => {
        const data = params.data as UnmatchedRecord;
        return (
          <span className="text-subtext" title={data.source_identifier}>
            {data.source_identifier || '-'}
          </span>
        );
      },
    },
    {
      field: 'status_code',
      headerName: t('table.status_code'),
      sortable: false,
      minWidth: 150,
      cellRenderer: (params: any) => {
        const data = params.data as UnmatchedRecord;
        const statusCode = data.raw_data?.status_code;
        return <span className="text-title">{statusCode || '-'}</span>;
      },
    },
    {
      field: 'status_description',
      headerName: t('table.status_description'),
      sortable: false,
      minWidth: 350,
      cellRenderer: (params: any) => {
        const data = params.data as UnmatchedRecord;
        const statusDescription = data.raw_data?.status_description;
        return (
          <span className="text-subtext" title={statusDescription}>
            {statusDescription || '-'}
          </span>
        );
      },
    },
    {
      field: 'date_tracking_update',
      headerName: t('table.date_tracking_update'),
      sortable: false,
      minWidth: 220,
      cellRenderer: (params: any) => {
        const data = params.data as UnmatchedRecord;
        const dateTrackingUpdate = data.raw_data?.date_tracking_update;
        if (!dateTrackingUpdate) return '-';
        try {
          return new Date(dateTrackingUpdate).toLocaleString('es-ES');
        } catch {
          return dateTrackingUpdate;
        }
      },
    },
    {
      field: 'created_at',
      headerName: t('table.created_at'),
      sortable: true,
      minWidth: 220,
      cellRenderer: (params: any) => {
        const data = params.data as UnmatchedRecord;
        if (!data.created_at) return '-';
        return new Date(data.created_at).toLocaleString('es-ES');
      },
    },
  ];
}
